import React, { FC, useCallback, useContext, useEffect, useState } from 'react'
import config from '@sketch/env-config'

import { noop } from '@sketch/utils'
import { useGetUserHubSpotIdentificationTokenLazyQuery } from '@sketch/gql-types'

const parseHubsJson = (jsonString: string) => {
  try {
    const o = JSON.parse(jsonString)
    if (o && typeof o === 'object') {
      return o
    }
  } catch (e) {
    return false
  }
  return false
}

type ContextProps = {
  loaded: boolean
  loadChat: () => void
  unloadChat: () => void
}

export const HubspotChatContext = React.createContext<ContextProps>({
  loaded: false,
  loadChat: noop,
  unloadChat: noop,
})

export const HubspotChatProvider: FC = ({ children }) => {
  const [isLoaded, setIsLoaded] = useState(false)

  const [
    getHubspotIdentificationToken,
  ] = useGetUserHubSpotIdentificationTokenLazyQuery({
    fetchPolicy: 'network-only',

    onError: () => {
      window.hsConversationsSettings = {
        loadImmediately: false,
      }
      window.HubSpotConversations?.widget.load({
        widgetOpen: false,
      })
    },
    onCompleted: data => {
      window.hsConversationsSettings = {
        identificationEmail: data.me?.email || '',
        identificationToken: data.me.hsIdentificationToken,
        loadImmediately: false,
      }
      window.HubSpotConversations?.widget.load({
        widgetOpen: false,
      })
    },
  })

  const onHubsMessage = useCallback(
    (e: { type: string; origin: string; data: any }) => {
      // Once open, the chat widget never goes away:
      // when the user hits the close "X" button, it gets minimized instead of being closed and removed from the screen
      // so we alter this by hooking into the messages the hubspot iframe emits when it minimizes the chat
      if (e.origin === 'https://app.hubspot.com') {
        const hubsLoad = parseHubsJson(e.data)
        if (
          hubsLoad &&
          hubsLoad.type === 'open-change' &&
          !hubsLoad.data.isOpen
        )
          unloadChat()
      }
      return
    },
    []
  )

  useEffect(() => {
    // Add event listener.
    window.hsConversationsOnReady = [
      () => {
        if (!isLoaded) {
          getHubspotIdentificationToken()
          setIsLoaded(true)
        }
      },
    ]

    return () => {
      window.hsConversationsOnReady = []
    }
  }, [getHubspotIdentificationToken, isLoaded])

  useEffect(() => {
    isLoaded && window.addEventListener('message', onHubsMessage)

    return () => {
      window.removeEventListener('message', onHubsMessage)
    }
  }, [isLoaded, onHubsMessage])

  const loadChat = useCallback(() => {
    window.hsConversationsSettings = {
      loadImmediately: false,
    }

    // The src attribute with the double forward slash is what's called a protocol-independent absolute path
    // and was taken from the HubSpot configuration instructions in case it interests to anyone it defaults the
    // scheme component to the one used by the browsers parent HTTPS in our case
    // see https://www.rfc-editor.org/rfc/rfc2396#section-5.2
    // documenting it here since the question popped up in the CR and we tough it may be interesting for anyone curious
    const src = `//js.hs-scripts.com/${config['hubspot_portal_id']}.js`
    let script = document.querySelector(`script[src="${src}"]`)

    if (
      window.HubSpotConversations &&
      !window.HubSpotConversations.widget.status().loaded
    ) {
      // if the script was already loaded we need to get the identification token again
      // since the user may have logged out and logged in with another one
      getHubspotIdentificationToken()
    }
    if (!script) {
      script = document.createElement('script')
      script.setAttribute('src', src)
      document.body.appendChild(script)
    }
  }, [getHubspotIdentificationToken])

  const unloadChat = () => {
    window.HubSpotConversations?.widget.remove()
    window.HubSpotConversations?.clear({ resetWidget: true })

    window.hsConversationsSettings = {
      loadImmediately: false,
    }

    setIsLoaded(false)
  }

  return (
    <HubspotChatContext.Provider
      value={{ loaded: isLoaded, unloadChat, loadChat }}
    >
      {children}
    </HubspotChatContext.Provider>
  )
}

export const useHubspotChat = () => {
  const { loaded, loadChat, unloadChat } = useContext(HubspotChatContext)

  return {
    loadChat,
    unloadChat,
    isLoaded: loaded,
  }
}
